import { z } from "zod";
import { evidenceAttachmentSchema } from "./contribution-evidence.js";
import { ledgerCorrectabilityShape } from "./ledger-correction.js";

/**
 * US-E24.F52-0504 (@UC9591) — NHÃN HIỂN THỊ của một dòng hoạt động "đã nộp quỹ".
 *
 * 🔴🔴 VÌ SAO KHÔNG SỬA THẲNG `ledger_transactions.memo`: bảng đó **append-only** (danh sách: `docs/generated/facts.md`)
 * (`reject_mutation()` cấm UPDATE/DELETE ở tầng DB) và **KHÔNG ĐƯỢC MỞ**. Nhãn là thứ người dùng
 * đổi tới đổi lui — bản chất LIFECYCLE — nên nó sống ở **bảng riêng đặt CẠNH sổ cái**
 * (`ledger_transaction_labels`), 0 cột mới trên sổ, 0 trigger nào bị nới.
 *
 * 🔴 NÓ LÀ CHỮ, KHÔNG PHẢI TIỀN. Hợp đồng này không có một field `*Minor` nào và không bao giờ
 * được có: đổi nhãn KHÔNG đổi một đồng nào của sổ. Sửa SỐ TIỀN vẫn đi đúng một đường
 * `POST /v1/funds/:id/ledger-corrections` (REVERSAL + POST trong một transaction).
 *
 * 🔴 MẶC ĐỊNH LÀ TÊN ĐỢT THU, KHÔNG PHẢI CHUỖI RỖNG. Chưa ai đặt nhãn ⇒ đọc ra `Collection.title`
 * của đợt thu sinh ra bút toán. Và đặt nhãn cho MỘT dòng **không** đổi tên đợt thu của cả nhóm —
 * đó là hai thứ khác nhau, gộp lại là một người sửa dòng của mình rồi cả nhóm thấy tên đợt đổi.
 */

/** Trần độ dài nhãn — mirror `Collection.title` (nhãn thay thế tên đợt thu, không dài hơn). */
export const LEDGER_TX_LABEL_MAX = 200;

/**
 * 🔴 US-E7.F1-0914 (@UC9591 · QĐ người dùng 07/10/2026 N4) — trần độ dài GHI CHÚ ĐÃ SỬA, khớp `payment_confirmations.note`
 * (ghi chú của lời khai đã chuyển). DB CHECK `ledger_transaction_labels_note_check` ép lần hai.
 */
export const LEDGER_TX_NOTE_MAX = 200;

/**
 * 🔴 US-E7.F1-0914 (review PR #1287 H2) — 422: PUT có `note` cho bút toán NGOÀI danh sách đóng loại sửa được ghi chú
 * (`LEDGER_TX_NOTE_EDITABLE_TX_TYPES`, domain). Cả lời gọi bị từ chối (kể cả `label` gửi kèm) — 0 hàng.
 */
export const LEDGER_TX_NOTE_NOT_EDITABLE_CODE = "LEDGER_TX_NOTE_NOT_EDITABLE" as const;

/**
 * Body `PUT /v1/funds/:id/transactions/:txId/label` — `{ label?, note? }`, ÍT NHẤT một field.
 *
 * 🔴 GỬI RỖNG = **BỎ** field đó (nhãn ⇒ về tên đợt thu; ghi chú ⇒ về ghi chú gốc của bút toán), KHÔNG phải "đặt = chuỗi rỗng".
 * Vì vậy schema CHO PHÉP chuỗi rỗng ở biên (một ý định hợp lệ) và `.trim()` để `"   "` cũng là bỏ — tầng DB có CHECK cấm lưu
 * rỗng, nên "rỗng" không bao giờ tồn tại ở dạng dữ liệu.
 *
 * 🔴 US-E7.F1-0914 N4 — **VẮNG = KHÔNG ĐỤNG**: nhãn và ghi chú độc lập, gửi một field không đổi field kia (bỏ nhãn KHÔNG xoá ghi
 * chú). Body `{}` ⇒ 400: một lời PUT không nói đổi gì là lời gọi hỏng, không phải no-op lặng lẽ.
 */
export const putTransactionLabelRequestSchema = z
  .object({
    label: z.string().trim().max(LEDGER_TX_LABEL_MAX).optional(),
    note: z.string().trim().max(LEDGER_TX_NOTE_MAX).optional(),
  })
  .strict()
  .refine((b) => b.label !== undefined || b.note !== undefined, {
    message: "Cần ít nhất một trường để cập nhật (label hoặc note)",
  });

export type PutTransactionLabelRequest = z.infer<typeof putTransactionLabelRequestSchema>;

/**
 * Nhãn HIỆU LỰC của một bút toán.
 *
 * `isDefault` là thứ client cần để biết mình đang nhìn nhãn RIÊNG hay tên đợt thu mượn tạm — thiếu
 * nó thì ô "Tiêu đề" trên form không phân biệt được *"chưa ai đặt"* với *"đã đặt trùng tên đợt"*,
 * và bấm Lưu sẽ ghi một nhãn mà người dùng không hề gõ.
 * `label = null` ⇒ không nhãn riêng VÀ không đợt thu nào để mượn tên (KHÔNG bịa chuỗi rỗng).
 */
export const transactionLabelResponseSchema = z
  .object({
    txId: z.string().uuid(),
    label: z.string().nullable(),
    isDefault: z.boolean(),
    /**
     * US-E24.F50-0793 — ĐỢT THU của bút toán nộp quỹ (qua `memberDueId` → `collections`), độc lập
     * với nhãn riêng: đặt nhãn "Tiền học kỳ 1" cho dòng thì màn chi tiết vẫn phải nói được khoản
     * này nộp cho đợt nào (ô Ghi chú chỉ-đọc của "Nộp quỹ"). `null` khi bút toán không gắn due
     * (thu khác / bút toán không có đợt). `collectionPeriodIndex` = kỳ thứ mấy của lịch, null với
     * đợt lẻ (cùng phép xếp hạng `loadPeriodIndexes`).
     * 🔴 Chỉ O/A/T nhận giá trị; member thường (route đọc mở cho mọi member) nhận `null` cả hai —
     * cùng luật ẩn với dòng feed cá nhân.
     */
    collectionTitle: z.string().nullable(),
    collectionPeriodIndex: z.number().int().positive().nullable(),
    /**
     * 🔴 US-E6.F3-0910 (@UC0057) — file bằng chứng của HOẠT ĐỘNG (`ledger_transactions.attachmentUrl`), link presigned
     * TTL ngắn. null khi không có file.
     */
    attachment: evidenceAttachmentSchema.nullable(),
    /**
     * 🔴 US-E7.F1-0914 (@UC9591 @UC9592 · N2/N3) — GHI CHÚ HIỆU LỰC của bút toán: ghi chú O/A/T đã sửa, chưa sửa ⇒ ghi chú gốc
     * (`memo`, đã lọc câu mặc định cũ — `effectiveTxNote`). CÙNG giá trị mọi bề mặt khác đang hiện cho bút toán này.
     * Cổng đọc như đợt thu/bằng chứng (`othersPaymentDetailVisible` — O/A/T luôn; member khi quỹ MỞ); không qua cổng ⇒ `null`.
     */
    note: z.string().nullable(),
    /**
     * `true` ⇒ chưa ai sửa ghi chú — `note` đang là ghi chú GỐC. Thiếu cờ này thì ô "Ghi chú" không phân biệt được *"chưa sửa"*
     * với *"đã sửa trùng câu gốc"* (cùng lý do với `isDefault` của nhãn).
     */
    noteIsDefault: z.boolean(),
    /**
     * 🔴 review PR #1287 H2 — bút toán này có thuộc loại SỬA ĐƯỢC ghi chú không (`LEDGER_TX_NOTE_EDITABLE_TX_TYPES`). Client mở ô
     * ghi chú theo cờ này (cùng vai O/A/T), KHÔNG tự suy từ loại dòng feed.
     */
    noteEditable: z.boolean(),
    /**
     * 🔴 FEED-CORRECTABLE-FLAG (@UC9297 @UC0036) — người xem sửa nhầm SỐ TIỀN bút toán này được không (`POST …/ledger-corrections`), tính bằng CÙNG luật
     * với cổng ghi. Client ẩn nút sửa số tiền theo cờ này; `notCorrectableReason` = vì sao (mã trung tính, `null` ⇔ sửa được).
     */
    ...ledgerCorrectabilityShape,
  })
  .strict();

export type TransactionLabelResponse = z.infer<typeof transactionLabelResponseSchema>;
